"use client";

import { useEffect, useMemo, useState } from "react";
import type { Dataset } from "../data";

const ui = {
  zh: {
    search: "检索作者、物种、阶段、技术或登录号",
    all: "全部物种",
    results: "个已核实数据集",
    cells: "细胞",
    source: "原始数据",
    processed: "作者处理结果",
    paper: "论文",
    atlas: "打开原作者图谱",
    portal: "打开本站交互图谱",
    cellBrowser: "打开专业基因表达图谱",
    records: "浏览样本与测序运行",
    empty: "没有符合条件的数据集。",
    verified: "来源已核实",
    countScope: "细胞数口径",
    passport: "科研数据护照",
    expression: "表达单位",
    coordinates: "坐标状态",
    reference: "参考版本",
    operation: "本站操作",
    licence: "数据许可",
    limitation: "已知边界",
    reviewed: "科学复核",
    recordVersion: "记录版本",
    filter: "按物种筛选",
  },
  en: {
    search: "Search author, species, stage, technology or accession",
    all: "All species",
    results: "verified data sets",
    cells: "cells",
    source: "Raw data",
    processed: "Author-processed",
    paper: "Paper",
    atlas: "Open author atlas",
    portal: "Open TrypCell view",
    cellBrowser: "Open professional expression atlas",
    records: "Browse samples and runs",
    empty: "No data sets match these filters.",
    verified: "Source verified",
    countScope: "Cell-count scope",
    passport: "Research data passport",
    expression: "Expression unit",
    coordinates: "Coordinate status",
    reference: "Reference version",
    operation: "Portal operation",
    licence: "Data licence",
    limitation: "Known boundary",
    reviewed: "Scientific review",
    recordVersion: "Record version",
    filter: "Filter by species",
  },
};

const availabilityLabels = {
  zh: {
    "interactive-atlas": "可交互图谱",
    "source-bounded-explorer": "来源限定浏览器",
    "provenance-only": "仅溯源记录",
  },
  en: {
    "interactive-atlas": "Interactive atlas",
    "source-bounded-explorer": "Source-bounded explorer",
    "provenance-only": "Provenance only",
  },
} as const;

export function DatasetExplorer({ datasets }: { datasets: Dataset[] }) {
  const [query, setQuery] = useState("");
  const [species, setSpecies] = useState("all");
  const [language, setLanguage] = useState<"en" | "zh">("zh");

  useEffect(() => {
    const handleLanguage = (event: Event) => {
      setLanguage((event as CustomEvent<"en" | "zh">).detail);
    };
    window.addEventListener("trypcell-language", handleLanguage);
    setLanguage(
      document.body.dataset.language === "en" ? "en" : "zh",
    );
    return () =>
      window.removeEventListener("trypcell-language", handleLanguage);
  }, []);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return datasets.filter((dataset) => {
      const matchesSpecies = species === "all" || dataset.species === species;
      const haystack = [
        dataset.species,
        dataset.speciesFull,
        dataset.speciesZh,
        dataset.author,
        dataset.year,
        dataset.technology,
        dataset.scopeEn,
        dataset.scopeZh,
        dataset.stagesEn,
        dataset.stagesZh,
        dataset.repository,
        dataset.processed,
        dataset.id,
        dataset.sourceRegistryId,
        dataset.recordStatusEn,
        dataset.recordStatusZh,
        dataset.expressionUnitEn,
        dataset.expressionUnitZh,
        dataset.coordinateStatusEn,
        dataset.coordinateStatusZh,
        dataset.referenceEn,
        dataset.referenceZh,
        dataset.limitationEn,
        dataset.limitationZh,
      ]
        .join(" ")
        .toLowerCase();
      return matchesSpecies && (!needle || haystack.includes(needle));
    });
  }, [datasets, query, species]);

  const t = ui[language];

  return (
    <div className="catalogue">
      <div className="catalogue-tools">
        <label className="search-field">
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.search}
            aria-label={t.search}
          />
        </label>
        <div className="segmented" aria-label={t.filter}>
          {[
            ["all", t.all],
            ["T. b. brucei", "T. b. brucei"],
            ["T. cruzi", "T. cruzi"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={species === value ? "active" : ""}
              onClick={() => setSpecies(value)}
              aria-pressed={species === value}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="catalogue-count" aria-live="polite">
        <strong>{visible.length}</strong> {t.results}
      </div>

      <div className="dataset-list">
        {visible.map((dataset) => (
          <article className="dataset-card" key={dataset.id}>
            <div className="dataset-index" aria-hidden="true">
              {dataset.species === "T. cruzi" ? "TC" : "TB"}
            </div>
            <div className="dataset-main">
              <div className="dataset-kicker">
                <i>{dataset.speciesFull}</i>
                <span>{dataset.year}</span>
                <span>{dataset.journal}</span>
              </div>
              <h2>
                {language === "zh" ? dataset.scopeZh : dataset.scopeEn}
              </h2>
              <p className="dataset-stages">
                {language === "zh" ? dataset.stagesZh : dataset.stagesEn}
              </p>
              <div className="dataset-meta">
                <span>
                  <strong>{dataset.cells.toLocaleString()}</strong> {t.cells}
                </span>
                <span>{dataset.technology}</span>
                <span>{dataset.author}</span>
              </div>
              <p className="dataset-count-basis">
                <strong>{t.countScope}:</strong>{" "}
                {language === "zh"
                  ? dataset.cellBasisZh
                  : dataset.cellBasisEn}
              </p>
              <details className="dataset-passport">
                <summary>
                  <span>{t.passport}</span>
                  <small>
                    {t.recordVersion} {dataset.recordVersion}
                  </small>
                </summary>
                <div className="dataset-passport-grid">
                  <div>
                    <span>{t.expression}</span>
                    <p>
                      {language === "zh"
                        ? dataset.expressionUnitZh
                        : dataset.expressionUnitEn}
                    </p>
                  </div>
                  <div>
                    <span>{t.coordinates}</span>
                    <p>
                      {language === "zh"
                        ? dataset.coordinateStatusZh
                        : dataset.coordinateStatusEn}
                    </p>
                  </div>
                  <div>
                    <span>{t.reference}</span>
                    <p>
                      {language === "zh"
                        ? dataset.referenceZh
                        : dataset.referenceEn}
                    </p>
                  </div>
                  <div>
                    <span>{t.operation}</span>
                    <p>
                      {language === "zh"
                        ? dataset.portalOperationZh
                        : dataset.portalOperationEn}
                    </p>
                  </div>
                  <div>
                    <span>{t.licence}</span>
                    <p>
                      {dataset.licenceEvidenceUrl ? (
                        <a
                          href={dataset.licenceEvidenceUrl}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {language === "zh"
                            ? dataset.dataLicenceZh
                            : dataset.dataLicenceEn}{" "}
                          ↗
                        </a>
                      ) : language === "zh" ? (
                        dataset.dataLicenceZh
                      ) : (
                        dataset.dataLicenceEn
                      )}
                    </p>
                  </div>
                  <div>
                    <span>{t.limitation}</span>
                    <p>
                      {language === "zh"
                        ? dataset.limitationZh
                        : dataset.limitationEn}
                    </p>
                  </div>
                </div>
              </details>
              <div className="dataset-links">
                <a href={dataset.doi} target="_blank" rel="noreferrer">
                  {t.paper} ↗
                </a>
                <a
                  href={dataset.repositoryUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.source}: {dataset.repository} ↗
                </a>
                <a
                  href={dataset.processedUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.processed}: {dataset.processed} ↗
                </a>
                {dataset.atlasUrl ? (
                  <a
                    href={dataset.atlasUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t.atlas} ↗
                  </a>
                ) : null}
                {dataset.portalUrl ? (
                  <a className="primary-link" href={dataset.portalUrl}>
                    {t.portal} →
                  </a>
                ) : null}
                {dataset.cellBrowserUrl ? (
                  <a className="primary-link" href={dataset.cellBrowserUrl}>
                    {t.cellBrowser} →
                  </a>
                ) : null}
                {dataset.recordsUrl ? (
                  <a className="records-link" href={dataset.recordsUrl}>
                    {t.records} →
                  </a>
                ) : null}
              </div>
            </div>
            <aside className="dataset-status">
              <span className="status-mark">✓</span>
              <strong>
                {language === "zh"
                  ? dataset.recordStatusZh
                  : dataset.recordStatusEn}
              </strong>
              <small>
                {availabilityLabels[language][dataset.availability]}
              </small>
              <small>
                {t.reviewed}: {dataset.lastVerified}
              </small>
            </aside>
          </article>
        ))}
        {visible.length === 0 ? (
          <div className="empty-state">{t.empty}</div>
        ) : null}
      </div>
    </div>
  );
}
